import React from 'react';
import styles from './CandidateCard.module.css';
import CandidateStatusBadge from './CandidateStatusBadge';
import ErrorModal from '../../components/Modals/ErrorModal';
import { getTimeUntilInterview } from '../../utils/interviewTimeUtils';
import { triggerOfferPdfDownload } from './api/offerLettersWithEdgeFunction';
import { Candidate, KanbanColumnId } from './types';
import { ATS_CONSTANTS } from './constants';

interface CandidateCardProps {
  candidate: Candidate;
  columnId: KanbanColumnId;
  jobStatus: string;
  onCardClick: (candidate: Candidate) => void;
  onGenerateOffer?: (candidate: Candidate) => void;
  isSelected?: boolean;
  onToggleSelection?: (id: string) => void;
  onUpdateStatus?: (candidate: Candidate, status: string) => void;
  onReschedule?: (candidate: Candidate) => void;
  onEditInterview?: (candidate: Candidate) => void;
  onCancelInterview?: (candidate: Candidate) => void;
}

const CandidateCard: React.FC<CandidateCardProps> = ({
  candidate,
  columnId,
  jobStatus,
  onCardClick,
  onGenerateOffer,
  isSelected,
  onToggleSelection,
}) => {
  const [isErrorModalOpen, setIsErrorModalOpen] = React.useState(false);
  const isClosed = jobStatus === 'Closed' || jobStatus === 'Filled';

  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCardClick(candidate);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelection) {
      onToggleSelection(candidate.id);
    }
  };

  return (
    <div
      className={`${styles.candidateCard} ${isSelected ? styles.candidateCardSelected : ''} ${styles.candidateCardBulkMode}`}
      onClick={handleCardClick}
      data-testid={`candidate-card-${candidate.id}`}
    >
      {!isClosed && onToggleSelection &&
        columnId !== ATS_CONSTANTS.COLUMNS.REJECTED &&
        columnId !== ATS_CONSTANTS.COLUMNS.ACCEPTED && (
          <div className={styles.selectionCheckbox} onClick={handleCheckboxClick} data-testid="candidate-card-checkbox">
            <input
              type="checkbox"
              checked={isSelected || false}
              readOnly
              style={{ pointerEvents: 'none' }} // Let the div handle the click
            />
          </div>
        )}

      <p className={styles.candidateCardName} data-testid="candidate-card-name">{candidate.name || 'Unknown Candidate'}</p>
      <p className={styles.candidateCardMatch} data-testid="candidate-card-match">
        {candidate.skillMatch > 0 ? `${candidate.skillMatch}% Skill Match` : 'AI Analysis: Calculating...'}
      </p>
      <p className={styles.candidateCardSkills}>
        Skills:{' '}
        {candidate.matchingSkills ? candidate.matchingSkills.join(', ') : 'No skills listed'}
      </p>

      <div className={styles.candidateCardActionFooter}>
        {columnId === ATS_CONSTANTS.COLUMNS.APPLIED && !isClosed && <div className={styles.emptyActionPlaceholder} />}

        {/* In Consideration section: Status badge (SCHEDULED/RESCHEDULED/COMPLETED/NO_SHOW) */}
        {columnId === ATS_CONSTANTS.COLUMNS.IN_CONSIDERATION && !isClosed && candidate.interviewDetails && (
          <div className={styles.interviewStatusContainer}>
            <div className={styles.interviewStatusRow}>
              <CandidateStatusBadge status={candidate.interviewDetails.status || ATS_CONSTANTS.STATUS.SCHEDULED} />
              {(() => {
                const status = candidate.interviewDetails?.status || ATS_CONSTANTS.STATUS.SCHEDULED;
                const timeUntil = getTimeUntilInterview(
                  candidate.interviewDetails?.date || '',
                  candidate.interviewDetails?.time || ''
                );
                return (status === ATS_CONSTANTS.STATUS.SCHEDULED || status === ATS_CONSTANTS.STATUS.RESCHEDULED) && timeUntil ? (
                  <span className={styles.interviewTimeUntil}>
                    Interview scheduled {timeUntil}
                  </span>
                ) : null;
              })()}
            </div>
            {candidate.interviewDetails.status === ATS_CONSTANTS.STATUS.COMPLETED && onGenerateOffer && (
              <div className={styles.emptyActionPlaceholder} />
            )}
          </div>
        )}

        {/* Accepted section: Only show if offer_letter exists, display offer_letter.status badge + offer buttons */}
        {columnId === ATS_CONSTANTS.COLUMNS.ACCEPTED && candidate.offerLetter && (
          <>
            <div className={styles.interviewStatusContainer}>
              <CandidateStatusBadge status={candidate.offerLetter.status || ATS_CONSTANTS.STATUS.GENERATED} />
            </div>
            {candidate.offerLetter.expires_at &&
              candidate.offerLetter.status !== ATS_CONSTANTS.STATUS.EXPIRED &&
              candidate.offerLetter.status !== ATS_CONSTANTS.STATUS.ACCEPTED && (
                <p className={styles.offerExpirationDate}>
                  Expires: {new Date(candidate.offerLetter.expires_at).toLocaleDateString()}
                </p>
              )}
            {
              candidate.offerLetter.status === ATS_CONSTANTS.STATUS.SYSTEM_REJECTED && (
                <span className={styles.interviewTimeUntil}>
                  Candidate has accepted another offer.
                </span>
              )}
            <button
              className={styles.buttonDownload}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await triggerOfferPdfDownload(candidate.id);
                } catch (error) {
                  console.error('Error downloading offer PDF:', error);
                  setIsErrorModalOpen(true);
                }
              }}
              data-testid="candidate-card-download-pdf"
            >
              Download PDF
            </button>
          </>
        )}


      </div>
      <ErrorModal
        isOpen={isErrorModalOpen}
        message="Failed to download offer letter"
        onClose={() => setIsErrorModalOpen(false)}
      />
    </div>
  );
};

export default CandidateCard;
